import type { ApiResponse } from '@bandos/shared'
import { getTraceData } from '@sentry/react'
import { handleAuthError } from '../auth/authErrorHandler'

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  if (!response.ok) {
    let errorData
    try {
      errorData = await response.json()
    } catch {
      // If response isn't JSON, create a descriptive error
      const statusMessages = {
        400: 'Bad Request - Please check your input',
        401: 'Authentication required - Please log in',
        403: "Access denied - You don't have permission",
        404: "Not found - The requested resource doesn't exist",
        429: 'Too many requests - Please wait and try again',
        500: 'Server error - Something went wrong on our end',
        502: 'Service unavailable - Please try again later',
        503: 'Service unavailable - Please try again later',
      }

      errorData = {
        error: `${response.status} Error`,
        message:
          statusMessages[response.status as keyof typeof statusMessages] ||
          'An unknown error occurred',
        code: `HTTP_${response.status}`,
      }
    }

    // Check for authentication errors (401) and handle logout + redirect
    const authErrorResult = handleAuthError(response, errorData)
    if (authErrorResult) {
      return authErrorResult
    }

    return {
      data: null,
      error: {
        message: errorData.message ?? errorData.error ?? 'An unknown error occurred',
        status: response.status,
        code: errorData.code,
        details: errorData.details,
      },
    }
  }

  try {
    const data = await response.json()
    return { data, error: null }
  } catch {
    return {
      data: null,
      error: {
        message: 'Invalid response format from server',
        status: response.status,
        code: 'INVALID_RESPONSE_FORMAT',
      },
    }
  }
}

export class HttpClient {
  private static async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      // Only set Content-Type header if there's a body to send
      const headers: Record<string, string> = {}

      // Handle different header types
      if (options.headers) {
        if (options.headers instanceof Headers) {
          options.headers.forEach((value, key) => {
            headers[key] = value
          })
        } else if (Array.isArray(options.headers)) {
          options.headers.forEach(([key, value]) => {
            headers[key] = value
          })
        } else {
          Object.assign(headers, options.headers)
        }
      }

      if (options.body) {
        headers['Content-Type'] = 'application/json'
      }

      // Distributed tracing: forward sentry-trace + baggage so the backend span
      // attaches to the same trace as the frontend transaction
      Object.assign(headers, getTraceData())

      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
      })
      return handleResponse<T>(response)
    } catch (error) {
      // Handle network/connection errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          data: null,
          error: {
            message: 'Unable to connect to server. Please check your internet connection.',
            code: 'NETWORK_ERROR',
            details: { endpoint: `${API_URL}${endpoint}` },
          },
        }
      }

      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Unknown network error',
          code: 'CONNECTION_FAILED',
        },
      }
    }
  }

  static async get<T>(endpoint: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', headers })
  }

  static async post<T>(
    endpoint: string,
    data?: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers,
    })
  }

  static async put<T>(
    endpoint: string,
    data: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers,
    })
  }

  static async patch<T>(
    endpoint: string,
    data: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers,
    })
  }

  static async delete<T>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', headers })
  }

  /**
   * Upload a file to the server using FormData
   */
  static async uploadFile<T>(
    endpoint: string,
    file: File,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          ...headers,
          ...getTraceData(),
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formData,
      })
      return handleResponse<T>(response)
    } catch (error) {
      // Handle network/connection errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          data: null,
          error: {
            message: 'Unable to connect to server. Please check your internet connection.',
            code: 'NETWORK_ERROR',
            details: { endpoint: `${API_URL}${endpoint}` },
          },
        }
      }

      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Unknown network error',
          code: 'CONNECTION_FAILED',
        },
      }
    }
  }
}
